// ChatConversation — the one conversation surface's shared inner, pure.
//
// The transcript-over-composer stack the ONE conversation shows: the
// ChatTranscript takes the height, and the PowerComposer (the one shared,
// owner-controlled draft) sits at the foot. ChatFull's body renders THIS
// component. The `density` prop keeps a `compact` reading contract as an
// orthogonal lever (exercised by stories); the live app renders at `full`. The
// "what grounds this + who it's for" chrome that used to stack on top (AiMarker,
// ChatContextChip, the SituationProfileChip banner) now lives in the
// ContextSideCard beside the full chat, so this inner is just transcript +
// composer plus the owner's slots.
//
// What varies between owners arrives as props, never as forks:
//   • `headStart` / `headEnd` — optional slots in the head row (ChatFull's
//     search input leads). The head row renders only when a slot is filled.
//   • `banner` — an optional row above the transcript (ChatFull's filtered-state
//     indicator).
//   • `status` / `onStop` / `onRetry` / `mentions` / `onMentionsChange` /
//     `candidates` / `commands` — PowerComposer's send-state machine and
//     @-mention/`/`-command vocabulary, forwarded untouched. All optional
//     here with inert defaults so an owner that hasn't wired live-turn state
//     yet (e.g. a story) still renders a working, idle composer.
//
// Composes ChatTranscript + PowerComposer. Pure/props-driven;
// chrome strings via Lingui; no I/O. One piece of local, ephemeral UI state:
// which suggestion row (keyed by the agent message id that offered it) the
// reader has dismissed — display-only, never reported up, and moot the moment
// a newer entry supersedes it (see `suggestions` below).

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useState } from "react";
import { readLastSeen, writeLastSeen } from "../../../app/lastSeenStore.js";
import { Button } from "../../Button/Button.js";
import { PowerComposer } from "../../Composer/PowerComposer/PowerComposer.js";
import type { PowerComposerStatus } from "../../Composer/PowerComposer/sendState.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../../Composer/PowerComposer/types.js";
import { EmptyState } from "../../EmptyState/EmptyState.js";
import type { FocusTarget, ResourceRef } from "../../Navigation/types.js";
import { ChatTranscript } from "../ChatTranscript/ChatTranscript.js";
import type { ResolveInlineLink } from "../InlineLink/InlineLink.js";
import { InterruptionNotice } from "../InterruptionNotice/InterruptionNotice.js";
import {
    type ChatAnchor,
    type ChatDensity,
    type ChatScope,
    isCompactAtRest,
} from "../presentation.js";
import {
    QuestionSheet,
    QuestionsWaitingBar,
} from "../QuestionSheet/QuestionSheet.js";
import { firstNewEntryId, latestTimestamp } from "../thread.js";
import type {
    ChatEntry,
    ChatEntryId,
    ChatFieldAnswer,
    DraftingPhase,
    FormSourceOption,
    InformationRequestEntry,
    InterruptionAction,
} from "../types.js";
import "./ChatConversation.css";

// The agent's opening-move offer as the surface renders it (EXPLICIT-START,
// onboarding-activation.md): a proactive "draft from your source" card the owner
// supplies only when it wants to offer. Named so ChatFull forwards the SAME
// shape without redeclaring the literal.
export type OpeningMoveOfferView = {
    readonly onAccept: () => void;
    // The source the first draft will be grounded on, when one is in focus.
    readonly sourceTitle?: string;
};

export type ChatConversationProps = {
    // Stable owner-supplied identity for THIS conversation. Entries are still
    // projected by the owner, but ephemeral view state must not leak when the
    // same surface switches from one conversation to another.
    readonly conversationId: string;
    // Already projected by the owner: scope (and any search) applied.
    readonly entries: readonly ChatEntry[];
    readonly scope: ChatScope;
    readonly anchor: ChatAnchor;
    // The reading contract threaded to the transcript (chat-ux.md §3): `full`
    // (default) is the roomy stack ChatFull renders; `compact` is the dense
    // quick-ask contract (kept as an orthogonal lever, exercised by stories).
    // The guidance (empty-state prompts, suggested-response chips) is identical
    // at either density.
    readonly density?: ChatDensity;
    // The in-flight/resolved status trailing the transcript.
    readonly drafting?: DraftingPhase;
    // The one shared draft, controlled by the owner.
    readonly draft: string;
    readonly onDraftChange: (value: string) => void;
    readonly onSubmit: () => void;
    // Re-drive the agent with a typed instruction (LANE S3). The reflexive
    // per-turn pill row is RETIRED; this handler now backs an ActionOffer's
    // `send_prompt` move (retry / re-export). Sends the text as the next turn.
    // Absent ⇒ send_prompt moves are omitted (no dead button). Kept named
    // `onSuggestedResponse` so the host's existing wiring (seed + send composer)
    // carries over unchanged.
    readonly onSuggestedResponse?: (text: string) => void;
    // Turn-epilogue form intents (LANE S3), forwarded to information_request
    // cards. `onSubmitForm` answers the durable ElicitationForm; `onDismissForm`
    // skips it. Absent ⇒ that affordance is omitted (no dead button).
    readonly onSubmitForm?: (
        requestId: string,
        answers: readonly ChatFieldAnswer[],
    ) => void;
    readonly onDismissForm?: (requestId: string) => void;
    // The library's sources a `field_sources` form question offers (D42).
    readonly formSources?: readonly FormSourceOption[];
    // The whole composer surface's own inert state (distinct from `status`
    // below — see PowerComposer's header note).
    readonly composerDisabled?: boolean;
    // The turn pipeline's state — idle/streaming/error — driving the send
    // button's identity (Send/Stop/Retry). Absent ⇒ idle (a caller with no
    // live turn state yet, e.g. a story, still renders a working composer).
    readonly status?: PowerComposerStatus;
    readonly onStop?: () => void;
    readonly onRetry?: () => void;
    // Message-history navigation (A-11), forwarded to PowerComposer. Arrow-Up
    // recalls an older sent message, Arrow-Down returns toward the draft; the
    // caret gating + menu precedence live in PowerComposer. Absent ⇒ arrow keys
    // are plain caret movement.
    readonly onRecallOlder?: () => void;
    readonly onRecallNewer?: () => void;
    readonly historyNavActive?: boolean;
    // The resources mentioned into the current draft via `@` — controlled,
    // so the owner can read the exact set to attach when the turn is sent.
    readonly mentions?: readonly ComposerResourceRef[];
    readonly onMentionsChange?: (
        mentions: readonly ComposerResourceRef[],
    ) => void;
    // The `@`-mentionable resource pool and the `/`-command vocabulary,
    // forwarded untouched (see PowerComposer's own header note — both are
    // data-driven, absent ⇒ an empty, still-working menu).
    readonly candidates?: readonly ComposerResourceRef[];
    readonly commands?: readonly ComposerSlashCommand[];
    // Proposal pointer intents, forwarded to proposal cards.
    readonly onGoToChange?: (id: ChatEntryId) => void;
    // A proposal card's Accept / Discard (by the proposal's id).
    readonly onAcceptProposal?: (proposalId: string) => void;
    readonly onDiscardProposal?: (proposalId: string) => void;
    readonly onReviewInPlace?: (id: ChatEntryId) => void;
    // Open a created material as a tab, forwarded to material cards.
    readonly onOpenMaterial?: (materialId: string) => void;
    // Open/download an exported package, forwarded to export cards.
    readonly onOpenExport?: (exportId: string) => void;
    readonly onDownloadExport?: (exportId: string) => void;
    // Open the shell's file picker for one Source (F9/F15's `request_upload`
    // move), forwarded to action-offer cards.
    readonly onRequestUpload?: (
        sourceId: string,
        sourceTitle: string | undefined,
    ) => void;
    // Open a cited source (cite-B), forwarded to the transcript's agent entries.
    readonly onOpenSource?: (resourceId: string) => void;
    readonly onOpenResource?: (ref: ResourceRef, target?: FocusTarget) => void;
    // Agent markdown links → resource chips (see InlineLink).
    readonly resolveLink?: ResolveInlineLink;
    // The interruption notice's recovery (forwarded to the transcript tail).
    readonly onInterruptionAction?: (action: InterruptionAction) => void;
    // Head-row slots around the chip (see the header note).
    readonly headStart?: ReactNode;
    readonly headEnd?: ReactNode;
    // An optional row between the head and the transcript.
    readonly banner?: ReactNode;
    // An optional leading affordance rendered before the composer's text
    // field (e.g. the "+" add-a-source menu — `ComposerAttachButton`),
    // forwarded straight through to PowerComposer's own `leadingSlot`. See
    // PowerComposer's header note: absent ⇒ nothing renders, so every other
    // caller is unaffected.
    readonly composerLeadingSlot?: ReactNode;
    // The agent's opening-move offer (EXPLICIT-START, onboarding-activation.md):
    // when present AND the conversation is empty, the empty surface leads with a
    // proactive "draft from your source" card whose primary action fires the
    // opening move. Absent ⇒ the generic empty-state hero. Owner decides whether
    // to offer (a pure projection of `shouldShowOpeningMoveOffer`); this
    // component only renders and reports the accept — it authors no command.
    readonly openingMoveOffer?: OpeningMoveOfferView;
    readonly className?: string;
};

export const ChatConversation = ({
    conversationId,
    entries,
    scope,
    anchor,
    density = "full",
    drafting,
    draft,
    onDraftChange,
    onSubmit,
    onSuggestedResponse,
    onSubmitForm,
    onDismissForm,
    formSources,
    composerDisabled,
    status = { type: "idle" },
    onStop = () => {},
    onRetry = () => {},
    onRecallOlder,
    onRecallNewer,
    historyNavActive,
    mentions = [],
    onMentionsChange = () => {},
    candidates,
    commands,
    onGoToChange,
    onAcceptProposal,
    onDiscardProposal,
    onReviewInPlace,
    onOpenMaterial,
    onOpenExport,
    onDownloadExport,
    onRequestUpload,
    onOpenSource,
    onOpenResource,
    resolveLink,
    onInterruptionAction,
    headStart,
    headEnd,
    banner,
    composerLeadingSlot,
    openingMoveOffer,
    className,
}: ChatConversationProps) => {
    const { t } = useLingui();

    // Compact rest (chat-ux.md §3, owner brief "basically an input box"): at
    // COMPACT density with no live turn the surface collapses to just its
    // composer — the transcript and the empty-state hero retire, and history is
    // reached through the box's expand controls. The rule is the pure
    // `isCompactAtRest`; the density gate is applied HERE so FULL density is
    // entirely untouched (atRestCompact is always false there).
    const atRestCompact = density === "compact" && isCompactAtRest(drafting);

    // "New since you were here" (pen "Chat – every entry" §9): where you left
    // off is read once per conversation, when its thread first shows, so the
    // line stays put while you read and send. View state on this device.
    const [newSince, setNewSince] = useState<
        { readonly conversationId: string; readonly id?: string } | undefined
    >(undefined);
    if (entries.length > 0 && newSince?.conversationId !== conversationId)
        setNewSince({
            conversationId,
            id: firstNewEntryId(entries, readLastSeen(conversationId)),
        });
    // What you have now seen: the newest message the thread shows.
    const seenUntil = latestTimestamp(entries);
    useEffect(() => {
        if (seenUntil !== undefined) writeLastSeen(conversationId, seenUntil);
    }, [conversationId, seenUntil]);
    // The assistant's question takes the composer's place (pen "Chat – every
    // entry" §4) until it is answered, skipped or set aside. Which questions
    // you set aside, skipped or just sent is view state (no domain meaning;
    // a sent answer shows in the thread once the log has it).
    const questionsInComposer =
        density === "full" && onSubmitForm !== undefined;
    const [asideIds, setAsideIds] = useState<ReadonlySet<string>>(new Set());
    const [skippedIds, setSkippedIds] = useState<ReadonlySet<string>>(
        new Set(),
    );
    const [sentIds, setSentIds] = useState<ReadonlySet<string>>(new Set());
    const add = (set: ReadonlySet<string>, id: string) => new Set([...set, id]);
    const without = (set: ReadonlySet<string>, id: string) =>
        new Set([...set].filter((x) => x !== id));
    const waiting = questionsInComposer
        ? entries.filter(
              (entry): entry is InformationRequestEntry =>
                  entry.type === "information_request" &&
                  entry.status.type === "pending" &&
                  !skippedIds.has(entry.id) &&
                  !sentIds.has(entry.id),
          )
        : [];
    const question = waiting.at(-1);
    const sheetOpen = question !== undefined && !asideIds.has(question.id);

    // An empty conversation is an INVITATION, not a void (T1): a warm hero line
    // plus object-scoped prompts the author can tap to seed the composer. Only
    // while there is nothing to read and nothing in flight — the moment a turn
    // starts (drafting) or any entry exists, the transcript takes over.
    const isEmpty = entries.length === 0 && drafting === undefined;
    // The resource the prompts speak to: the scoped resource, else a pinned
    // one, else none (workspace/project scope → generic copy, no prompts).
    const focus: ResourceRef | undefined =
        scope.type === "resource"
            ? scope.resource
            : anchor.type === "pinned"
              ? anchor.resource
              : undefined;
    // Short, author-voice first-run prompts per resource kind. Tapping one
    // fills the composer (never auto-sends) so the author adjusts before asking.
    const focusKind = focus?.type;
    const emptyHero: {
        readonly title: string;
        readonly body: string;
        readonly prompts: readonly string[];
    } = (() => {
        switch (focusKind) {
            case "source":
                return {
                    title: t`Ask about this source`,
                    body: t`The assistant can summarise, explain, and create material from this source.`,
                    prompts: [
                        t`Summarise this source`,
                        t`What are the key points?`,
                        t`Create practice questions from the source`,
                    ],
                };
            case "material":
                return {
                    title: t`Ask about this material`,
                    body: t`The assistant can improve, review, and build on this material.`,
                    prompts: [
                        t`Suggest improvements`,
                        t`Create more questions`,
                        t`Review the difficulty level`,
                    ],
                };
            case "export":
            case "file":
            case undefined:
                return {
                    title: t`Ask the assistant`,
                    body: t`The assistant helps you create and improve teaching material from your sources.`,
                    prompts: [],
                };
            default:
                return exhaustSwitchCase(focusKind);
        }
    })();

    return (
        <div
            className={`bilda-chat-conversation${className ? ` ${className}` : ""}`}
            data-conversation-id={conversationId}
            data-density={density}
        >
            {headStart !== undefined || headEnd !== undefined ? (
                // The head carries only the owner's slots now — the scope/AI
                // chrome that used to live here (AiMarker + ChatContextChip)
                // moved into the ContextSideCard beside the full chat, so the
                // transcript starts higher and nothing duplicates the sidecard.
                <div className="bilda-chat-conversation__head">
                    {headStart}
                    {headEnd !== undefined ? (
                        <div className="bilda-chat-conversation__head-end">
                            {headEnd}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {banner !== undefined ? (
                // The banner slot owns the horizontal gutter (css-formatting-audit)
                // so its content — the SituationProfileChip, the filtered-state row
                // — lines up with the head + suggestions instead of bleeding edge-to-edge.
                // Fixes both mount points (assistant tab + docked chat) at once.
                <div className="bilda-chat-conversation__banner">{banner}</div>
            ) : null}

            {atRestCompact ? null : isEmpty ? (
                <div className="bilda-chat-conversation__empty">
                    {openingMoveOffer !== undefined ? (
                        // The aha bridge (onboarding-activation.md §4d): the
                        // agent OFFERS to draft; accepting fires the opening
                        // move. One dominant primary, no auto-run — the author
                        // clicks to start (EXPLICIT-START).
                        <EmptyState
                            title={
                                openingMoveOffer.sourceTitle !== undefined
                                    ? t`Draft teaching material from ${openingMoveOffer.sourceTitle}?`
                                    : t`Draft teaching material from your source?`
                            }
                            body={t`The assistant will read your source and start a draft you can review and edit.`}
                        >
                            <Button
                                variant="primary"
                                onClick={openingMoveOffer.onAccept}
                            >
                                {t`Draft it`}
                            </Button>
                        </EmptyState>
                    ) : (
                        <EmptyState
                            title={emptyHero.title}
                            body={emptyHero.body}
                        >
                            {emptyHero.prompts.length > 0 ? (
                                <div className="bilda-chat-conversation__prompts">
                                    {emptyHero.prompts.map((prompt) => (
                                        <Button
                                            key={prompt}
                                            variant="secondary"
                                            size="md"
                                            onClick={() =>
                                                onDraftChange(prompt)
                                            }
                                        >
                                            {prompt}
                                        </Button>
                                    ))}
                                </div>
                            ) : null}
                        </EmptyState>
                    )}
                </div>
            ) : (
                <ChatTranscript
                    className="bilda-chat-conversation__transcript"
                    entries={entries}
                    density={density}
                    drafting={drafting}
                    onGoToChange={onGoToChange}
                    onAcceptProposal={onAcceptProposal}
                    onDiscardProposal={onDiscardProposal}
                    onReviewInPlace={onReviewInPlace}
                    onOpenMaterial={onOpenMaterial}
                    onOpenExport={onOpenExport}
                    onDownloadExport={onDownloadExport}
                    onSubmitForm={onSubmitForm}
                    onDismissForm={onDismissForm}
                    formSources={formSources}
                    onSendPrompt={onSuggestedResponse}
                    onRequestUpload={onRequestUpload}
                    onOpenSource={onOpenSource}
                    onOpenResource={onOpenResource}
                    resolveLink={resolveLink}
                    onInterruptionAction={onInterruptionAction}
                    newSinceId={
                        newSince?.conversationId === conversationId
                            ? newSince?.id
                            : undefined
                    }
                    questionsInComposer={questionsInComposer}
                    skippedQuestions={skippedIds}
                    onAnswerNow={(id) => {
                        setSkippedIds((set) => without(set, id));
                        setAsideIds((set) => without(set, id));
                    }}
                    ariaLabel={t`Conversation with the assistant`}
                />
            )}

            {/* F-03: a turn is running and the author has typed a new
                message — say it is kept, and offer the way to send it now. */}
            {status?.type === "streaming" &&
            draft.trim() !== "" &&
            onStop !== undefined ? (
                <InterruptionNotice
                    interruption={{ type: "typing_while_working" }}
                    onAction={onStop}
                />
            ) : null}

            {question !== undefined && sheetOpen ? (
                <QuestionSheet
                    key={question.id}
                    entry={question}
                    sources={formSources}
                    onSubmit={(answers) => {
                        onSubmitForm?.(question.id, answers);
                        setSentIds((set) => add(set, question.id));
                    }}
                    onSkip={() => {
                        onDismissForm?.(question.id);
                        setSkippedIds((set) => add(set, question.id));
                    }}
                    onSetAside={() =>
                        setAsideIds((set) => add(set, question.id))
                    }
                />
            ) : null}
            {question !== undefined && !sheetOpen ? (
                <QuestionsWaitingBar
                    count={waiting.length}
                    onAnswer={() =>
                        setAsideIds((set) => without(set, question.id))
                    }
                />
            ) : null}
            {sheetOpen ? null : (
                <PowerComposer
                    value={draft}
                    onValueChange={onDraftChange}
                    leadingSlot={composerLeadingSlot}
                    mentions={mentions}
                    onMentionsChange={onMentionsChange}
                    status={status}
                    onSend={onSubmit}
                    onStop={onStop}
                    onRetry={onRetry}
                    onRecallOlder={onRecallOlder}
                    onRecallNewer={onRecallNewer}
                    historyNavActive={historyNavActive}
                    candidates={candidates}
                    commands={commands}
                    disabled={composerDisabled}
                    // The conversation composer sits at the FOOT of the transcript
                    // in every presentation (full, docked, compact float), so the
                    // slash/mention menus flip UP into the free space above rather
                    // than shoving the composer off the viewport's bottom edge.
                    menuPlacement="above"
                />
            )}
        </div>
    );
};
